// Facebook API
import { getAPI, token } from "@/api/params.js";
import { makeJsonRequest, getTimestampedUrl } from "@/api/common.js";

// Facebook OAuth authentication URL
const FACEBOOK_API_ENDPOINT = "https://connect.facebook.net/en_US/sdk.js";
// Facebook App Id
// eslint-disable-next-line no-undef
const FACEBOOK_APP_ID = process.env.VUE_APP_FACEBOOK_APP_ID;
// Facebook API version number
const FACEBOOK_API_VERSION = "v19.0";

// List of Facebook app permissions
const FACEBOOK_SCOPES = [
  "public_profile",
  "email",
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  // "publish_to_groups",
  "business_management",
  "instagram_basic",
  "instagram_content_publish"
];

export const FACEBOOK_CATEGORIES = {
  BEAUTY_FASHION: "Beauty & Fashion",
  BUSINESS: "Business",
  CARS_TRUCKS: "Cars & Trucks",
  COMEDY: "Comedy",
  CUTE_ANIMALS: "Cute animals",
  ENTERTAINMENT: "Entertainment",
  FAMILY: "Family",
  FOOD_HEALTH: "Food & Health",
  HOME: "Home",
  LIFESTYLE: "Lifestyle",
  MUSIC: "Music",
  NEWS: "News",
  POLITICS: "Politics",
  SCIENCE: "Science",
  SPORTS: "Sports",
  TECHNOLOGY: "Technology",
  VIDEO_GAMING: "Video gaming",
  OTHER: "Other"
};

const API_PREFIX = `${getAPI()}/user/socials/facebook`;

initFacebookAPI();

/**
 * Connect to service new Facebook account via OAuth process
 */
export const authFacebook = async () => {
  await new Promise((resolve, reject) => {
    try {
      // eslint-disable-next-line no-undef
      FB.login(
        async response => {
          try {
            const reqURL = `${API_PREFIX}/connect?token=${token}`;
            const accessToken = response.authResponse.accessToken;
            await makeJsonRequest({
              url: reqURL,
              method: "POST",
              data: { accessToken: accessToken }
            });
            resolve();
          } catch(e) {
            this.showError("Socials Initialize Error", e);
            reject(e);
          }
        },
        { scope: FACEBOOK_SCOPES.join() }
      );
    } catch (e) {
      // this.showError("Socials Initialize Error", e);
      reject(e);
    }
  });
};

/**
 * Upload video to Facebook
 * @param {Number} videoId Video ID
 * @param {Number} accountId Internal account ID
 * @param {String} facebookId Social account ID
 * @param {String} facebookType Type of sharing destination (page or group)
 * @param {Object} facebookData Facebook video metadata: title, description, contentCategory, noStory & sharing time
 */
export const shareToFacebook = async (
  videoId,
  accountId,
  facebookId,
  facebookType,
  facebookData,
  multistepId
) => {
  const shareURL = getTimestampedUrl(
    getSharingURL(videoId, accountId, facebookId, facebookType, multistepId),
    facebookData.scheduledTime
  );
  await makeJsonRequest({
    url: shareURL,
    method: "POST",
    data: {
      title: facebookData.title,
      description: facebookData.description
    }
  });
};

/**
 * Get Facebook account additional data from API server
 * @param {Object} accountId Internal account ID
 * @param {String} method Facebook data getting method (info/groups/accounts)
 */
export const fetchFacebookData = async (accountId, method) => {
  const requestURL = `${API_PREFIX}/${accountId}/${method}?token=${token}`;

  const resp = await fetch(requestURL);
  const respObj = await resp.json();

  if (!resp.ok) {
    throw new Error(`${respObj.error.code}: ${respObj.error.message}`);
  }

  return respObj;
};

/**
 * Return Facebook share API URL
 * @param {Number} videoId Internal video ID
 * @param {Number} accountId Internal account ID
 * @param {String} facebookId Social account ID
 * @param {String} dest group or page
 */
function getSharingURL(videoId, accountId, facebookId, dest, multistepId) {
  if (multistepId === undefined) {
    return `${getAPI()}/video/${videoId}/share/facebook?token=${token}&social=${accountId}&fbId=${facebookId}&fbType=${dest}`;
  } else {
    return `${getAPI()}/video/${videoId}/share/facebook?token=${token}&social=${accountId}&fbId=${facebookId}&fbType=${dest}&multistepId=${multistepId}`;
  }
}

/**
 * Initialize Facebook API
 */
function initFacebookAPI() {
  const fbAPIScript = document.createElement("script");
  fbAPIScript.setAttribute("src", FACEBOOK_API_ENDPOINT);
  fbAPIScript.onload = () => {
    window.fbAsyncInit = () => {
      // eslint-disable-next-line no-undef
      FB.init({
        appId: FACEBOOK_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: FACEBOOK_API_VERSION
      });
    };
  };
  document.head.appendChild(fbAPIScript);
}
